/**
 * Query inspection and filtering for React Query context registration.
 * Determines which queries should be registered and infers their metadata.
 */

import { IGNORED_QUERY_KEYS } from './config';
import { getAIContextConfig, getFirstQueryKey, inferScope, inferType, parseQueryKeyParts } from './helpers';

import type { ContextConfig } from './types';
import type { ContextMetadata, ContextPriority } from '../../context/types';
import type { QueryKey } from '@tanstack/react-query';

/**
 * Infer context metadata from a React Query key.
 *
 * Query key format: ['resource-action', params]
 * Examples:
 * - ['sales-summary', {...}] → scope: 'sales', type: 'sales-data'
 * - ['product-detail', {...}] → scope: 'products', type: 'product-data'
 * - ['customer-list', {...}] → scope: 'customers', type: 'customer-data'
 */
export function inferContextMetadata(queryKey: QueryKey): Partial<ContextMetadata> {
  if (!Array.isArray(queryKey) || queryKey.length === 0) {
    return { type: 'other', priority: 'medium' };
  }

  // Parse query key into parts
  const { baseName } = parseQueryKeyParts(queryKey);
  const firstKey = getFirstQueryKey(queryKey);

  // Infer scope and type from base name
  const scope = inferScope(baseName);
  const type = inferType(baseName);

  // Infer priority (default to medium, can be customized)
  const priority: ContextPriority = 'medium';

  // Generate ID from query key
  const id = `query-${firstKey}`;

  return {
    id,
    scope,
    type,
    priority,
  };
}

/**
 * Check if a query should be auto-registered as context.
 *
 * @param queryKey - The React Query key
 * @param meta - Query metadata (may contain aiContext config)
 * @returns true if should register, false otherwise
 */
export function shouldRegisterQuery(queryKey: QueryKey, meta?: unknown): boolean {
  // Skip if query key is empty
  if (!Array.isArray(queryKey) || queryKey.length === 0) {
    return false;
  }

  // Check if query key should be ignored
  const firstKey = getFirstQueryKey(queryKey);
  const shouldIgnore = IGNORED_QUERY_KEYS.some((pattern) => firstKey.includes(pattern));
  if (shouldIgnore) {
    // console.log('[React Query Context] Ignoring query key:', firstKey);
    return false;
  }

  // Check if explicitly disabled via aiContext config
  const aiContextConfig = getAIContextConfig(meta);

  if (aiContextConfig === false) {
    return false;
  }

  if (typeof aiContextConfig === 'object' && aiContextConfig.enabled === false) {
    return false;
  }

  // Log queries that ARE being registered (temporary debug log)
  console.log('[React Query Context] Registering query key:', firstKey);

  // Default: register the query
  return true;
}

/**
 * Get context configuration from query metadata.
 * Merges inferred metadata with explicit config.
 *
 * @param queryKey - React Query key
 * @param data - Query result data
 * @param meta - Query metadata
 * @returns Context configuration or null if should not register
 */
export function getContextConfig(queryKey: QueryKey, data: unknown, meta?: unknown): ContextConfig | null {
  if (!shouldRegisterQuery(queryKey, meta)) {
    return null;
  }

  // Start with inferred metadata
  const inferred = inferContextMetadata(queryKey);

  // Get explicit config from meta
  const aiContextConfig = getAIContextConfig(meta);
  const explicit = typeof aiContextConfig === 'object' ? aiContextConfig : {};

  // Merge: explicit overrides inferred
  const metadata: Partial<ContextMetadata> = {
    ...inferred,
    ...explicit,
    source: 'react-query',
  };

  // Generate final ID
  const firstKey = getFirstQueryKey(queryKey);
  const id = explicit.id || inferred.id || `query-${firstKey}`;

  return {
    id,
    metadata,
    data,
  };
}
